import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import {
  getApiSystemVersion,
  getGetApiSystemVersionQueryKey,
} from '../generated/endpoints/system/system';

/**
 * The backend's own version, for the apps' version overlay (HS-1208).
 *
 * Hand-written rather than generated: the committed OpenAPI snapshot declares
 * `GET /api/System/version` with a bare `200 OK` and no response content, so Orval types
 * `getApiSystemVersion` as `Promise<void>` and there is no DTO to import. The fetcher and the
 * query key still come from the generated module, so the URL and the cache key stay in one
 * place; only the response shape is asserted here, against the live payload
 * `{"version":"0.6.0.0","success":true}`.
 *
 * That body carries no `data` member, so `customInstance` passes it through without envelope
 * unwrapping — what the fetcher resolves to is the whole object.
 *
 * Remove this module once the spec describes the response and codegen can type it.
 */
const apiVersionSchema = z.object({
  version: z.string().min(1),
});

/** One retry: the overlay is informational, so a flaky network should not mean repeated calls. */
const API_VERSION_RETRY = 1;

/**
 * The generated fetcher is typed `Promise<void>` because the spec declares no response content,
 * but the endpoint does return a body. Re-type it once here rather than casting at the call site.
 */
const fetchVersionBody = getApiSystemVersion as unknown as (
  options?: undefined,
  signal?: AbortSignal,
) => Promise<unknown>;

/**
 * Query options for the backend version, split out from the hook so callers can prefetch it
 * and tests can drive it through `fetchQuery` without a React tree — the same split the
 * generated `get*QueryOptions` helpers use.
 *
 * The value cannot change while the tab is open — a deployed backend swap reloads nothing —
 * so it is fetched once and never revalidated.
 */
export function getApiVersionQueryOptions(): UseQueryOptions<string> {
  return {
    queryKey: getGetApiSystemVersionQueryKey(),
    queryFn: async ({ signal }) => {
      const body = await fetchVersionBody(undefined, signal);
      return apiVersionSchema.parse(body).version;
    },
    staleTime: Infinity,
    retry: API_VERSION_RETRY,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  };
}

/**
 * Reads the backend version.
 *
 * Failure is not exceptional here: callers render the app's own version and simply omit the
 * API segment when `data` is undefined.
 */
export function useApiVersion(): UseQueryResult<string> {
  return useQuery(getApiVersionQueryOptions());
}
